import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiSearch } from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import './AdminPages.css';
import './SearchAutoSuggestions.css';

/** Keep in sync with backend SUGGEST_ATTRIBUTES — always render even if API is slow/fails. */
const ATTRIBUTE_DEFS = [
  { key: 'property_id', label: 'Property ID', description: 'Show and match public property numbers (e.g. #12345)' },
  { key: 'property_name', label: 'Property Name', description: 'Listing / project name' },
  { key: 'company_name', label: 'Company / Developer Name', description: 'Show company name as a chip under property suggestions (company rows always appear when matched)' },
  { key: 'bhk', label: 'BHK', description: 'Bedroom configuration' },
  { key: 'price', label: 'Price', description: 'Sale or rent price' },
  { key: 'land_area', label: 'Land Area', description: 'Plot / land / built-up area' },
  { key: 'floor_number', label: 'Floor Number', description: 'Property floor number' },
  { key: 'total_floors', label: 'Total Floors', description: 'Building total floors' },
  { key: 'car_parking', label: 'Car Parking', description: 'Car parking availability' },
  { key: 'furnishing', label: 'Furnishing Status', description: 'Furnished / semi / unfurnished' },
  { key: 'category', label: 'Property Type / Category', description: 'Category name (e.g. Apartment, Villa)' },
  { key: 'location', label: 'Location', description: 'Address / map location text' },
  { key: 'city', label: 'City', description: 'City name' },
];

const defaultFlags = () => {
  const flags = {};
  ATTRIBUTE_DEFS.forEach((a) => { flags[a.key] = false; });
  return flags;
};

const mergeFlags = (raw = {}) => {
  const next = defaultFlags();
  ATTRIBUTE_DEFS.forEach((a) => {
    if (raw[a.key] !== undefined) next[a.key] = !!raw[a.key];
  });
  return next;
};

const SearchAutoSuggestions = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [attributes, setAttributes] = useState(() => defaultFlags());

  const headers = () => ({ Authorization: `Bearer ${getAuthToken()}` });

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/search-suggest-settings`, { headers: headers() });
      const fromApi = res.data?.attributes;
      const fromDefs = Array.isArray(res.data?.attributeDefs) ? res.data.attributeDefs : null;
      // Prefer server flags; always keep full local defs so the list never goes empty
      if (fromDefs?.length) {
        // If server adds new keys later, still merge known ones
        setAttributes(mergeFlags(fromApi || {}));
      } else {
        setAttributes(mergeFlags(fromApi || {}));
      }
    } catch (e) {
      setAttributes(defaultFlags());
      toast.error(e.response?.data?.error || 'Failed to load search suggestion settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const persist = async (next, keyHint = null) => {
    const prev = { ...attributes };
    setAttributes(next);
    setSaving(true);
    setSavingKey(keyHint);
    try {
      const res = await axios.put(
        `${API_URL}/admin/search-suggest-settings`,
        { attributes: next },
        { headers: headers() }
      );
      setAttributes(mergeFlags(res.data?.attributes || next));
      return true;
    } catch (e) {
      setAttributes(prev);
      toast.error(e.response?.data?.error || 'Failed to save setting');
      return false;
    } finally {
      setSaving(false);
      setSavingKey(null);
    }
  };

  const toggleAttribute = async (key, enabled) => {
    const next = { ...attributes, [key]: enabled };
    const ok = await persist(next, key);
    if (ok) toast.success(enabled ? 'Attribute enabled in suggestions' : 'Attribute disabled in suggestions');
  };

  const setAll = async (enabled) => {
    const next = defaultFlags();
    ATTRIBUTE_DEFS.forEach((a) => { next[a.key] = enabled; });
    const ok = await persist(next, 'all');
    if (ok) toast.success(enabled ? 'All attributes enabled' : 'All attributes disabled');
  };

  const enabledCount = useMemo(
    () => Object.values(attributes).filter(Boolean).length,
    [attributes]
  );

  return (
    <div className="admin-page sas-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1><FiSearch style={{ marginRight: 8 }} />Search Auto Suggestions</h1>
          <p>Choose which property attributes appear as chips under Home and Properties search suggestions. Company / Developer names always appear as their own suggestion rows when matched.</p>
        </div>
        <div className="sas-header-actions">
          <button type="button" className="btn-secondary" onClick={() => setAll(true)} disabled={loading || saving}>
            Enable All
          </button>
          <button type="button" className="btn-secondary" onClick={() => setAll(false)} disabled={loading || saving}>
            Disable All
          </button>
          <button type="button" className="btn-secondary" onClick={loadSettings} disabled={loading || saving}>
            <FiRefreshCw /> Refresh
          </button>
        </div>
      </div>

      <div className="admin-card sas-card">
        <div className="sas-summary">
          <strong>{enabledCount}</strong> of {ATTRIBUTE_DEFS.length} attributes enabled
        </div>

        {loading ? (
          <div className="skeleton" style={{ height: 280, borderRadius: 8 }} />
        ) : (
          <div className="sas-list">
            {ATTRIBUTE_DEFS.map((def) => {
              const on = !!attributes[def.key];
              return (
                <div key={def.key} className={`sas-row ${on ? 'is-on' : ''}`}>
                  <div className="sas-row-text">
                    <h3>{def.label}</h3>
                    <p>{def.description}</p>
                  </div>
                  <label className={`toggle-label${savingKey === def.key ? ' disabled' : ''}`} style={{ flexShrink: 0 }}>
                    <span>{on ? 'On' : 'Off'}</span>
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={saving}
                      onChange={(e) => toggleAttribute(def.key, e.target.checked)}
                    />
                    <div className="toggle-switch" />
                  </label>
                </div>
              );
            })}
          </div>
        )}

        <p className="sas-hint">
          Suggestions always show property thumbnails/titles and matching Company / Developer rows. Enabled attributes appear as detail chips under each property result. Maximum 5 suggestions are returned.
        </p>
      </div>
    </div>
  );
};

export default SearchAutoSuggestions;
